// src/pages/Login.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';
import OTPInput from '../components/OTPInput';
import LMSHeader from '../components/LMSHeader';
import { useToast } from '../components/ToastContainer';

const Login = () => {
  const navigate = useNavigate();
  const user = useAuthStore(state => state.user);
  const toast = useToast();
  const { sendOTP, verifyOTP, login, isLoading, error, clearError } = useAuthStore();

  const [showPassword, setShowPassword] = useState(false);

  const [loginMethod, setLoginMethod] = useState(''); // 'otp' or 'password'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState(''); // New state for password
  const [otp, setOtp] = useState('');
  const [errors, setErrors] = useState({});
  const [sentOtp, setSentOtp] = useState(false); // Track if OTP was sent

  const rolePaths = {
    student: '/student/dashboard',
    instructor: '/instructor/dashboard',
    admin: '/admin/cloud/dashboard',
    organization: '/org/dashboard', // Add organization path
    school: '/school/dashboard'    // Add school path if applicable
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, toast, clearError]);

  useEffect(() => {
    if (user && user.role) {
      toast.info('You are already authenticated!');
      navigate(rolePaths[user.role] || '/');
    }
  }, [user, navigate, rolePaths]);

  const validateEmail = () => {
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
    }
    return newErrors;
  };

  const validatePassword = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    return newErrors;
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    const emailErrors = validateEmail();
    setErrors(emailErrors);

    if (Object.keys(emailErrors).length > 0) {
      toast.error(Object.values(emailErrors)[0]);
      return;
    }

    const result = await sendOTP(email);
    if (result.success) {
      setSentOtp(true);
      toast.success('OTP sent successfully!');
    }
  };

  const handleVerifyOTP = async (otpValue) => {

    const result = await verifyOTP(
      email,
      otpValue,
    );

    if (result.success) {
      toast.success('Logged in successfully!');
      const redirectUrl = localStorage.getItem('redirectAfterLogin');

      if (typeof window !== 'undefined' && window.Tawk_API) {
        window.Tawk_API.setAttributes({
          name: result?.user?.name || result?.user?.email.split('@')[0],
          email: result?.user?.email,
        });
      }

      if (redirectUrl) {
        navigate(redirectUrl);
        localStorage.removeItem('redirectAfterLogin');
      } else {
        navigate(rolePaths[result.user.role] || '/');
      }
    }
  };

  const handleLoginWithPassword = async (e) => {
    e.preventDefault();
    const emailErrors = validateEmail();
    const passwordErrors = validatePassword();
    const allErrors = { ...emailErrors, ...passwordErrors };
    setErrors(allErrors);

    if (Object.keys(allErrors).length > 0) {
      toast.error(Object.values(allErrors)[0]);
      return;
    }

    const result = await login(email, password);
    if (result.success) {
      toast.success('Logged in successfully!');
      const redirectUrl = localStorage.getItem('redirectAfterLogin');

      if (typeof window !== 'undefined' && window.Tawk_API) {
        window.Tawk_API.setAttributes({
          name: result?.user?.name || result?.user?.email.split('@')[0],
          email: result?.user?.email,
        });
      }

      if (redirectUrl) {
        navigate(redirectUrl);
        localStorage.removeItem('redirectAfterLogin');
      } else {
        navigate(rolePaths[result.user.role] || '/');
      }
    }
  };

  const handleBackToMethodSelection = () => {
    setLoginMethod(null);
    setSentOtp(false);
    setOtp('');
    setPassword('');
    setErrors({});
  };

  const handleBackToEmail = () => {
    setSentOtp(false);
    setOtp('');
    setErrors({});
  };

  if (user) {
    return null; // Or a loading spinner if redirect takes time
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col relative">
      <LMSHeader />

      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('/images/login-bg-two.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
      </div>

      <main className="flex-1 flex items-center justify-center p-4 relative z-10">
        <div className="max-w-md w-full bg-white dark:bg-neutral-800 rounded-xl shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 backdrop-blur-sm">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
              Welcome Back
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Sign in to continue your learning journey
            </p>
          </div>

          {error && (
            <div className="mb-6 p-3 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-danger-300 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Method Selection Step (Initial) */}
          {!loginMethod && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-4">Choose Login Method</h2>
              <div className="space-y-4">
                <button
                  onClick={() => setLoginMethod('password')}
                  className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Login with Email & Password
                </button>
                <button
                  onClick={() => setLoginMethod('otp')}
                  className="w-full py-3 px-4 bg-white border border-neutral-300 hover:bg-neutral-300 dark:bg-neutral-700 dark:border-neutral-500 dark:hover:bg-neutral-600 text-neutral-900 dark:text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-neutral-500"
                >
                  Login with Email OTP
                </button>
              </div>
            </div>
          )}

          {/* Login with Password Step */}
          {loginMethod === 'password' && (
            <form onSubmit={handleLoginWithPassword} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`w-full px-4 py-3 border rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 ${errors.email
                    ? 'border-danger-500 focus:ring-danger-500'
                    : 'border-neutral-300 dark:border-neutral-600 focus:ring-primary-500'
                    }`}
                  placeholder="your.email@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-danger-500">{errors.email}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Password
                </label>

                <div className="relative w-full">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={`w-full px-4 py-3 border rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 ${errors.password
                      ? 'border-danger-500 focus:ring-danger-500'
                      : 'border-neutral-300 dark:border-neutral-600 focus:ring-primary-500'
                      }`}
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-danger-500">{errors.password}</p>
                  )}
                  <span
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer select-none text-xl"
                  >
                    {showPassword ? '🛡️' : '🔐'}
                  </span>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm disabled:opacity-50"
              >
                {isLoading ? 'Logging in...' : 'Login'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToMethodSelection}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Back to Login Methods
                </button>
              </div>
            </form>
          )}

          {/* Login with OTP Step */}
          {loginMethod === 'otp' && !sentOtp && (
            <form onSubmit={handleSendOTP} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={`w-full px-4 py-3 border rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 ${errors.email
                    ? 'border-danger-500 focus:ring-danger-500'
                    : 'border-neutral-300 dark:border-neutral-600 focus:ring-primary-500'
                    }`}
                  placeholder="your.email@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-danger-500">{errors.email}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm disabled:opacity-50"
              >
                {isLoading ? 'Sending OTP...' : 'Send OTP'}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToMethodSelection}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Back to Login Methods
                </button>
              </div>
            </form>
          )}

          {/* OTP Verification Step */}
          {loginMethod === 'otp' && sentOtp && (
            <div className="space-y-6 relative min-h-[200px] flex flex-col items-center justify-center">
              {isLoading ? (
                <div className="absolute inset-0 flex justify-center items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
              ) : (
                <>
                  <div className="text-center">
                    <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                      Enter OTP
                    </h2>
                    <p className="text-neutral-600 dark:text-neutral-400">
                      We've sent a 6-digit code to {email}
                    </p>
                  </div>

                  <div className="flex justify-center">
                    <OTPInput
                      length={6}
                      onChange={setOtp}
                      onComplete={handleVerifyOTP}
                    />
                  </div>

                  <div className="text-center space-y-2">
                    <button
                      type="button"
                      onClick={handleBackToEmail}
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
                    >
                      Back
                    </button>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">
                      Didn't receive OTP?{' '}
                      <button
                        type="button"
                        onClick={handleSendOTP} // Resend OTP
                        disabled={isLoading}
                        className="text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        Resend
                      </button>
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-neutral-200 dark:border-neutral-700 text-center">
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              Don&apos;t have an account?{' '}
              <button
                onClick={() => navigate('/register')}
                className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
              >
                Register
              </button>
            </p>
            {loginMethod && loginMethod === 'password' && (
              <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                Forgot your password?{' '}
                <button
                  onClick={() => navigate('/change-password')}
                  className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                >
                  Reset Password
                </button>
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;